/**
 * KizunaAI billing guide: balance, what blocks a session, session segments,
 * the Both-mode shared session and the other side's voice.
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app, except
 * 03-shared-session.png (v0.44.0).
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'Balance, Costs and Session Time',
  backLink: 'Back to KizunaAI Quick Start',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Overview',
    content:
      'KizunaAI is paid from a prepaid balance on your Kizuna AI account, by use, with no subscription; both engines, Soniox and Qwen, draw on that balance at their own rates. This page explains where to see your balance, why Start can be disabled, what happens when a session reaches its limit, and which settings change the cost.',
  },
  steps: [
    {
      title: 'Check your balance',
      content:
        'The account button in the title bar shows your balance. Click it to see your email address, your balance and what you spent in the last 30 days. <strong>Top up</strong> opens the billing page of your Kizuna AI account in your browser, where you add funds and see the current rates before you pay.',
      screenshot: '01-balance.png',
    },
    {
      title: 'When Start is disabled',
      content:
        'Sokuji only starts a session when your balance covers at least its first minute. If it does not, the Start button is greyed out, hovering over it shows <strong>Insufficient balance</strong> with your balance, and the account button carries a red dot. Top up and try again.',
      screenshot: '02-start-blocked.png',
      tip: 'Spoken translation needs a higher balance to start than subtitles only, and more again when the other side is read aloud to you too.',
    },
    {
      title: 'Soniox and Qwen are metered differently',
      content:
        'The Soniox engine is charged for the time a session is connected, for the speech it recognizes and for the speech it synthesizes. The Qwen engine is charged per token: the audio it hears, the text it writes and the audio it speaks, so a quiet session costs little and spoken translation costs more than subtitles. With screen context on, the Qwen engine is also charged for the pictures it sees, per second of speech, in each direction. Qwen’s Beijing region is priced in Chinese yuan and converted to US dollars at the day’s rate when it is charged. In every case you pay for actual use, and the rates are on the billing page before you top up.',
    },
    {
      title: 'During a session',
      content:
        'A session runs against part of your balance, and Sokuji counts down the time left. Long sessions run in segments: when one ends, Sokuji says <strong>This segment has ended</strong> and you click <strong>Start Session</strong> to continue. When your balance is used up, the session stops and asks you to top up.',
    },
    {
      title: 'Both mode: shared or separate sessions',
      content:
        'With the Soniox engine in Both mode, <strong>Shared session in Both mode</strong> (Settings → Advanced → Provider) decides the cost. <strong>Disabled</strong> (the default) runs one session per direction, which is more reliable when both people talk at once, but costs about twice as much per minute, so your balance lasts about half as long and a higher balance is needed to start. <strong>Enabled</strong> translates both sides in one session with automatic voice separation, at lower cost and latency. A face-to-face conversation always uses one shared session. The Qwen engine has no shared session: Both mode always runs one session per direction.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Hearing the other side read aloud',
      content:
        'With <strong>Translation I hear</strong> switched on (Other or Both mode), KizunaAI reads the other side’s translation to you in a second voice. That voice is charged like your own spoken translation, so the session costs more per minute and needs a higher balance to start. In a face-to-face conversation with voice, both halves are spoken: your translation in the other person’s ear, and theirs in yours.',
    },
  ],
  tips: {
    title: 'Ways to spend less',
    items: [
      'When you only need subtitles, switch off Translation the other side hears, and leave Translation I hear off unless you want the other side read aloud: generating speech costs extra.',
      'With the Soniox engine, turn on Shared session in Both mode to save, unless both sides often talk at the same time.',
      'Stop the session when the conversation ends: a running session keeps using your balance.',
      'Accounts with a verified email address get a trial credit (currently US$0.50, valid for 7 days).',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'Is there a subscription?',
        answer: 'No. You top up your balance and pay only for what you use.',
      },
      {
        question: 'Where can I see the rates?',
        answer: 'On the billing page of your Kizuna AI account, which Top up opens. The rates are shown there before you pay.',
      },
      {
        question: 'The app says “Another session is already running on your account”.',
        answer: 'One account runs one KizunaAI session at a time, whichever engine it uses. Stop the session on your other device or window, then try again.',
      },
      {
        question: 'Previewing a voice asks me to top up.',
        answer: 'Voice previews are synthesized and charged to your balance, so they need a balance too.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Questions about a charge? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: '余额、费用与会话时长',
  backLink: '返回 KizunaAI 快速入门',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概述',
    content:
      'KizunaAI 从你 Kizuna AI 账号中的预付余额按用量扣费，无需订阅；Soniox 和 Qwen 两个引擎都从这笔余额中扣费，各按自己的费率。本页说明在哪里查看余额、“开始”为什么可能不可用、会话达到上限时会发生什么，以及哪些设置会影响费用。',
  },
  steps: [
    {
      title: '查看余额',
      content:
        '标题栏中的账号按钮会显示你的余额。点击它可以查看你的邮箱地址、余额以及最近 30 天的消费。<strong>充值</strong>会在浏览器中打开你 Kizuna AI 账号的账单页面，你可以在那里充值，并在付款前查看当前费率。',
      screenshot: '01-balance.png',
    },
    {
      title: '“开始”不可用时',
      content:
        '只有当余额至少够支付会话的第一分钟时，Sokuji 才会开始会话。余额不够时，“开始”按钮会变灰，把鼠标悬停在上面会显示<strong>余额不足</strong>和你的余额，账号按钮上也会出现红点。充值后再试一次。',
      screenshot: '02-start-blocked.png',
      tip: '语音翻译开始时所需的余额比只有字幕时更高；如果对方的翻译也要朗读给你听，所需余额还会更高。',
    },
    {
      title: 'Soniox 和 Qwen 的计量方式不同',
      content:
        'Soniox 引擎按会话连接的时长、识别的语音和合成的语音计费。Qwen 引擎按 token 计费：它听到的音频、写出的文字和说出的音频，所以安静的会话花费很少，语音翻译比仅字幕花费更多。开启屏幕上下文时，Qwen 引擎还会按语音的秒数为它看到的画面计费，每个方向各计一次。Qwen 的北京区域以人民币计价，收费时按当日汇率换算成美元。无论哪种情况，你都只为实际用量付费，充值前可以在账单页面查看费率。',
    },
    {
      title: '会话进行中',
      content:
        '会话会占用你余额中的一部分，Sokuji 会倒计时显示剩余时间。较长的会话会分段进行：一段结束时，Sokuji 会提示<strong>本段已结束</strong>，点击<strong>开始会话</strong>即可继续。余额用完时，会话会停止并提示你充值。',
    },
    {
      title: '两者模式：共享会话还是独立会话',
      content:
        '使用 Soniox 引擎的两者模式下，<strong>「两者」模式下共享会话</strong>（设置 → 高级 → 提供商）决定费用。<strong>禁用</strong>（默认）时，每个方向各运行一个会话，在双方同时说话时更可靠，但每分钟费用约为两倍，因此余额可用时间大约减半，开始时也需要更高的余额。<strong>已启用</strong>时，一个会话同时翻译双方，并自动区分声音，成本和延迟都更低。面对面对话始终使用同一个共享会话。Qwen 引擎没有共享会话：两者模式始终每个方向各运行一个会话。',
      screenshot: '03-shared-session.png',
    },
    {
      title: '把对方的话朗读给你听',
      content:
        '打开<strong>我听到的翻译</strong>后（对方或两者模式），KizunaAI 会用第二个声音把对方的翻译朗读给你听。这个声音和你自己的语音翻译一样计费，因此会话每分钟的费用更高，开始时也需要更高的余额。在带语音的面对面对话中，双方的翻译都会朗读出来：你的翻译读进对方的耳朵，对方的翻译读进你的耳朵。',
    },
  ],
  tips: {
    title: '省钱技巧',
    items: [
      '只需要字幕时，请关闭“对方听到的翻译”；除非你想听对方的翻译，否则让“我听到的翻译”保持关闭：生成语音会额外计费。',
      '使用 Soniox 引擎时，除非双方经常同时说话，否则打开“「两者」模式下共享会话”可以省钱。',
      '对话结束后请停止会话：正在运行的会话会持续消耗你的余额。',
      '完成邮箱验证的账号可获得试用额度（目前为 US$0.50，有效期 7 天）。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '需要订阅吗？',
        answer: '不需要。你只需充值余额，按实际用量付费。',
      },
      {
        question: '在哪里可以查看费率？',
        answer: '在你 Kizuna AI 账号的账单页面，点击“充值”即可打开。付款前，费率会显示在那里。',
      },
      {
        question: '应用提示“您的账户中已有另一个会话正在进行”。',
        answer: '一个账号同一时间只能运行一个 KizunaAI 会话，无论使用哪个引擎。请停止你在其他设备或窗口上的会话，然后重试。',
      },
      {
        question: '试听声音时提示我充值。',
        answer: '声音试听需要实时合成，并从你的余额中扣费，因此同样需要有余额。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '对扣费有疑问？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。',
  },
};

const ja: TutorialData = {
  pageTitle: '残高・料金・セッション時間',
  backLink: 'KizunaAI クイックスタートに戻る',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '概要',
    content:
      'KizunaAI の料金は、Kizuna AI アカウントのプリペイド残高から使った分だけ支払います。サブスクリプションはなく、Soniox と Qwen の 2 つのエンジンはどちらもこの残高から、それぞれの料金で差し引かれます。このページでは、残高の確認場所、「開始」が押せなくなる理由、セッションが上限に達したときの動作、料金に影響する設定について説明します。',
  },
  steps: [
    {
      title: '残高を確認する',
      content:
        'タイトルバーのアカウントボタンに残高が表示されます。クリックすると、メールアドレス、残高、過去 30 日間の利用額を確認できます。<strong>チャージ</strong>をクリックすると、ブラウザーで Kizuna AI アカウントの請求ページが開きます。そこで残高を追加でき、支払い前に現在の料金も確認できます。',
      screenshot: '01-balance.png',
    },
    {
      title: '「開始」が押せないとき',
      content:
        'Sokuji は、残高で少なくともセッションの最初の 1 分をまかなえる場合にだけセッションを開始します。まかなえない場合は「開始」ボタンがグレーになり、マウスポインターを重ねると残高とともに<strong>残高不足</strong>と表示され、アカウントボタンに赤い点が付きます。チャージしてから、もう一度お試しください。',
      screenshot: '02-start-blocked.png',
      tip: '音声で翻訳する場合は、字幕のみよりも開始に必要な残高が多くなります。相手の翻訳もあなたに読み上げる場合は、さらに多くなります。',
    },
    {
      title: 'Soniox と Qwen では課金方式が違います',
      content:
        'Soniox エンジンは、セッションが接続されていた時間、認識した音声、合成した音声に対して課金されます。Qwen エンジンはトークン単位で課金されます。聞き取った音声、書き出したテキスト、話した音声が対象なので、静かなセッションはほとんど費用がかからず、音声での翻訳は字幕のみより高くなります。画面コンテキストがオンのときは、Qwen エンジンが見た画像についても、発話の秒数に応じて方向ごとに課金されます。Qwen の北京リージョンは中国人民元建てで、課金時にその日のレートで米ドルに換算されます。いずれの場合も実際に使った分だけを支払い、料金はチャージの前に請求ページで確認できます。',
    },
    {
      title: 'セッション中',
      content:
        'セッションは残高の一部を使って実行され、Sokuji が残り時間をカウントダウンします。長いセッションはセグメントに分けて実行されます。1 つのセグメントが終わると Sokuji が<strong>このセグメントは終了しました</strong>と表示するので、<strong>セッション開始</strong>をクリックして続けます。残高を使い切るとセッションは停止し、チャージを求められます。',
    },
    {
      title: '両方モード：共有セッションか別々のセッションか',
      content:
        'Soniox エンジンの両方モードでは、<strong>「両方」モードでセッションを共有</strong>（設定 → 詳細 → プロバイダー）が料金を左右します。<strong>無効</strong>（既定）では方向ごとに 1 つずつセッションを実行します。2 人が同時に話すときはより安定しますが、1 分あたりの料金がおよそ 2 倍になるため、残高の持ちはおよそ半分になり、開始に必要な残高も多くなります。<strong>有効</strong>では、1 つのセッションで双方を翻訳し、声を自動的に区別するので、コストと遅延が低くなります。対面での会話では、常に 1 つの共有セッションを使います。Qwen エンジンには共有セッションがなく、両方モードでは常に方向ごとに 1 つずつセッションを実行します。',
      screenshot: '03-shared-session.png',
    },
    {
      title: '相手の翻訳を読み上げで聞く',
      content:
        '<strong>自分に聞こえる翻訳</strong>をオンにすると（相手モードまたは両方モード）、KizunaAI は相手の翻訳を 2 つ目の音声であなたに読み上げます。この音声はあなた自身の翻訳音声と同じように課金されるため、セッションの 1 分あたりの料金が上がり、開始に必要な残高も多くなります。音声ありの対面での会話では、両方の翻訳が読み上げられます。あなたの翻訳は相手の耳に、相手の翻訳はあなたの耳に届きます。',
    },
  ],
  tips: {
    title: '費用を抑えるには',
    items: [
      '字幕だけでよい場合は「相手に聞こえる翻訳」をオフにし、相手の翻訳を読み上げで聞きたいのでなければ「自分に聞こえる翻訳」もオフのままにしてください。音声の生成には追加料金がかかります。',
      'Soniox エンジンでは、双方が同時に話すことが多い場合を除き、「「両方」モードでセッションを共有」をオンにすると費用を抑えられます。',
      '会話が終わったらセッションを停止してください。実行中のセッションは残高を消費し続けます。',
      'メールアドレスを認証したアカウントには試用クレジット（現在 US$0.50、有効期間 7 日）が付きます。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: 'サブスクリプションはありますか？',
        answer: 'いいえ。残高をチャージし、使った分だけ支払います。',
      },
      {
        question: '料金はどこで確認できますか？',
        answer: '「チャージ」で開く Kizuna AI アカウントの請求ページです。支払う前に、そこで料金が表示されます。',
      },
      {
        question: '「このアカウントでは既に別のセッションが実行中です」と表示されます。',
        answer: '1 つのアカウントで同時に実行できる KizunaAI セッションは、どちらのエンジンでも 1 つだけです。ほかのデバイスやウィンドウのセッションを停止してから、もう一度お試しください。',
      },
      {
        question: '音声を試聴しようとするとチャージを求められます。',
        answer: '音声の試聴はその都度合成され、残高から差し引かれるため、試聴にも残高が必要です。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      '請求についてご不明な点がある場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。',
  },
};

const ko: TutorialData = {
  pageTitle: '잔액, 비용, 세션 시간',
  backLink: 'KizunaAI 빠른 시작으로 돌아가기',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: '개요',
    content:
      'KizunaAI는 Kizuna AI 계정의 선불 잔액에서 사용한 만큼 결제되며, 구독은 없습니다. 두 엔진인 Soniox와 Qwen 모두 각자의 요금으로 그 잔액에서 차감됩니다. 이 페이지에서는 잔액을 확인하는 곳, “시작”이 비활성화되는 이유, 세션이 한도에 도달하면 어떻게 되는지, 비용을 바꾸는 설정은 무엇인지 설명합니다.',
  },
  steps: [
    {
      title: '잔액 확인',
      content:
        '제목 표시줄의 계정 버튼에 잔액이 표시됩니다. 클릭하면 이메일 주소, 잔액, 최근 30일간 사용한 금액을 볼 수 있습니다. <strong>충전</strong>을 클릭하면 브라우저에서 Kizuna AI 계정의 결제 페이지가 열리며, 여기서 금액을 충전하고 결제 전에 현재 요금을 확인할 수 있습니다.',
      screenshot: '01-balance.png',
    },
    {
      title: '“시작”이 비활성화될 때',
      content:
        'Sokuji는 잔액이 적어도 세션의 첫 1분을 감당할 수 있을 때만 세션을 시작합니다. 그렇지 않으면 “시작” 버튼이 회색으로 바뀌고, 마우스를 올리면 잔액과 함께 <strong>잔액 부족</strong>이 표시되며, 계정 버튼에 빨간 점이 붙습니다. 충전한 다음 다시 시도하세요.',
      screenshot: '02-start-blocked.png',
      tip: '음성 번역은 자막만 사용할 때보다 시작하는 데 더 많은 잔액이 필요하며, 상대방의 말까지 소리 내어 읽어 줄 때는 더 많이 필요합니다.',
    },
    {
      title: 'Soniox와 Qwen은 과금 방식이 다릅니다',
      content:
        'Soniox 엔진은 세션이 연결되어 있던 시간, 인식한 음성, 합성한 음성에 대해 요금이 부과됩니다. Qwen 엔진은 토큰 단위로 요금이 부과됩니다. 들은 오디오, 쓴 텍스트, 말한 오디오가 각각 대상이므로, 조용한 세션은 비용이 적게 들고 음성 번역은 자막만 사용할 때보다 비용이 더 듭니다. 화면 컨텍스트가 켜져 있으면 Qwen 엔진은 보는 이미지에 대해서도 음성 초 단위로, 방향마다 요금이 부과됩니다. Qwen의 베이징 지역은 중국 위안으로 가격이 책정되며, 요금이 부과될 때 그날의 환율로 미국 달러로 환산됩니다. 어떤 경우든 실제로 사용한 만큼만 지불하며, 요금은 충전하기 전에 결제 페이지에서 확인할 수 있습니다.',
    },
    {
      title: '세션 중',
      content:
        '세션은 잔액의 일부를 할당받아 실행되며, Sokuji가 남은 시간을 카운트다운합니다. 긴 세션은 여러 세그먼트로 나뉘어 실행됩니다. 한 세그먼트가 끝나면 Sokuji가 <strong>이 세그먼트가 종료되었습니다</strong>라고 알려 주며, <strong>세션 시작</strong>을 클릭하면 계속됩니다. 잔액을 다 쓰면 세션이 중지되고 충전하라는 안내가 나옵니다.',
    },
    {
      title: '모두 모드: 공유 세션 또는 별도 세션',
      content:
        'Soniox 엔진의 모두 모드에서는 <strong>모두 모드에서 세션 공유</strong>(설정 → 고급 → 제공자)가 비용을 결정합니다. <strong>비활성화</strong>(기본값)이면 방향마다 세션을 하나씩 실행합니다. 두 사람이 동시에 말할 때 더 안정적이지만 분당 비용이 약 두 배이므로, 잔액이 약 절반의 시간만 유지되고 시작하는 데 더 많은 잔액이 필요합니다. <strong>활성화됨</strong>이면 하나의 세션이 양쪽을 번역하고 음성을 자동으로 구분하므로 비용과 지연이 낮습니다. 마주 보고 대화할 때는 항상 하나의 공유 세션을 사용합니다. Qwen 엔진에는 공유 세션이 없으며, 모두 모드는 항상 방향마다 세션을 하나씩 실행합니다.',
      screenshot: '03-shared-session.png',
    },
    {
      title: '상대방의 말을 소리 내어 듣기',
      content:
        '<strong>내가 듣는 번역</strong>을 켜면(상대방 또는 모두 모드) KizunaAI가 상대방의 번역을 두 번째 음성으로 나에게 읽어 줍니다. 이 음성은 내 음성 번역과 같은 방식으로 요금이 부과되므로, 세션의 분당 비용이 늘어나고 시작하는 데 더 많은 잔액이 필요합니다. 음성을 사용하는 마주 보고 대화에서는 양쪽 모두 음성으로 나옵니다. 내 번역은 상대방의 귀에, 상대방의 번역은 내 귀에 들립니다.',
    },
  ],
  tips: {
    title: '비용을 줄이는 방법',
    items: [
      '자막만 필요할 때는 “상대방에게 들리는 번역”을 끄고, 상대방의 말을 소리 내어 듣고 싶은 경우가 아니라면 “내가 듣는 번역”도 꺼 둔 채로 두세요. 음성 생성에는 추가 비용이 듭니다.',
      'Soniox 엔진에서는 양쪽이 자주 동시에 말하는 경우가 아니라면 “모두 모드에서 세션 공유”를 켜서 비용을 줄이세요.',
      '대화가 끝나면 세션을 중지하세요. 실행 중인 세션은 계속 잔액을 사용합니다.',
      '이메일 주소를 인증한 계정에는 체험 크레딧(현재 US$0.50, 7일간 유효)이 제공됩니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '구독이 있나요?',
        answer: '아니요. 잔액을 충전하고 사용한 만큼만 지불합니다.',
      },
      {
        question: '요금은 어디서 볼 수 있나요?',
        answer: '“충전”을 누르면 열리는 Kizuna AI 계정의 결제 페이지에서 볼 수 있습니다. 결제하기 전에 그곳에 요금이 표시됩니다.',
      },
      {
        question: '앱에 “이미 계정에서 다른 세션이 실행 중입니다”라고 표시됩니다.',
        answer: '하나의 계정은 어떤 엔진을 쓰든 한 번에 하나의 KizunaAI 세션만 실행할 수 있습니다. 다른 기기나 창에서 실행 중인 세션을 중지한 다음 다시 시도하세요.',
      },
      {
        question: '음성을 미리 들으려고 하면 충전하라고 합니다.',
        answer: '음성 미리 듣기는 합성되어 잔액에서 차감되므로, 미리 듣기에도 잔액이 필요합니다.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '요금에 대해 궁금한 점이 있나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요.',
  },
};

const es: TutorialData = {
  pageTitle: 'Saldo, costes y duración de la sesión',
  backLink: 'Volver al inicio rápido de KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Descripción general',
    content:
      'KizunaAI se paga con un saldo prepago de tu cuenta de Kizuna AI, según el uso y sin suscripción; los dos motores, Soniox y Qwen, usan ese saldo a sus propias tarifas. Esta página explica dónde ver tu saldo, por qué Iniciar puede estar desactivado, qué ocurre cuando una sesión llega a su límite y qué ajustes cambian el coste.',
  },
  steps: [
    {
      title: 'Consulta tu saldo',
      content:
        'El botón de la cuenta, en la barra de título, muestra tu saldo. Haz clic en él para ver tu dirección de correo, tu saldo y lo que has gastado en los últimos 30 días. <strong>Recargar</strong> abre en tu navegador la página de facturación de tu cuenta de Kizuna AI, donde añades fondos y puedes ver las tarifas actuales antes de pagar.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Cuando Iniciar está desactivado',
      content:
        'Sokuji solo inicia una sesión cuando tu saldo cubre al menos su primer minuto. Si no es así, el botón Iniciar aparece en gris, al pasar el ratón por encima se muestra <strong>Saldo insuficiente</strong> junto con tu saldo, y el botón de la cuenta lleva un punto rojo. Recarga y vuelve a intentarlo.',
      screenshot: '02-start-blocked.png',
      tip: 'La traducción hablada necesita más saldo para empezar que solo subtítulos, y más aún cuando también se te lee en voz alta lo que dice la otra parte.',
    },
    {
      title: 'Soniox y Qwen se miden de forma distinta',
      content:
        'El motor de Soniox cobra por el tiempo que una sesión está conectada, por el habla que reconoce y por el habla que sintetiza. El motor de Qwen cobra por token: el audio que oye, el texto que escribe y el audio que habla, así que una sesión tranquila cuesta poco y la traducción hablada cuesta más que los subtítulos. Con el contexto de pantalla activado, el motor de Qwen también cobra por las imágenes que ve, por cada segundo de voz y en cada dirección. La región de Pekín de Qwen se factura en yuanes chinos y se convierte a dólares estadounidenses al tipo de cambio del día en que se cobra. En todos los casos pagas por el uso real, y las tarifas están en la página de facturación antes de que recargues.',
    },
    {
      title: 'Durante una sesión',
      content:
        'Una sesión funciona con una parte de tu saldo, y Sokuji muestra una cuenta atrás del tiempo restante. Las sesiones largas se dividen en segmentos: cuando termina uno, Sokuji indica <strong>Este segmento ha terminado</strong> y haces clic en <strong>Iniciar sesión</strong> para continuar. Cuando se agota tu saldo, la sesión se detiene y te pide que recargues.',
    },
    {
      title: 'Modo Ambos: sesión compartida o sesiones separadas',
      content:
        'Con el motor de Soniox, en el modo Ambos, <strong>Sesión compartida en el modo Ambos</strong> (Configuración → Avanzado → Proveedor) determina el coste. Con <strong>Deshabilitado</strong> (el valor predeterminado) se ejecuta una sesión por dirección, lo que es más fiable cuando ambas personas hablan a la vez, pero cuesta aproximadamente el doble por minuto, así que tu saldo dura más o menos la mitad y se necesita más saldo para empezar. Con <strong>Habilitado</strong>, una sola sesión traduce ambos lados con separación automática de voces, con menor coste y latencia. Una conversación cara a cara siempre usa una sola sesión compartida. El motor de Qwen no tiene sesión compartida: el modo Ambos siempre ejecuta una sesión por dirección.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Oír en voz alta la traducción de la otra parte',
      content:
        'Con <strong>Traducción que yo oigo</strong> activada (modo Otro o Ambos), KizunaAI te lee la traducción de la otra parte con una segunda voz. Esa voz se cobra igual que tu propia traducción hablada, así que la sesión cuesta más por minuto y necesita más saldo para empezar. En una conversación cara a cara con voz se pronuncian las dos mitades: tu traducción en el oído de la otra persona, y la suya en el tuyo.',
    },
  ],
  tips: {
    title: 'Formas de gastar menos',
    items: [
      'Cuando solo necesites subtítulos, desactiva Traducción que oye la otra parte, y deja desactivada Traducción que yo oigo salvo que quieras oír en voz alta a la otra parte: generar voz cuesta más.',
      'Con el motor de Soniox, activa Sesión compartida en el modo Ambos para ahorrar, salvo que ambos lados hablen a menudo a la vez.',
      'Detén la sesión cuando termine la conversación: una sesión en marcha sigue consumiendo tu saldo.',
      'Las cuentas con una dirección de correo verificada reciben un crédito de prueba (actualmente 0,50 US$, válido durante 7 días).',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Hay suscripción?',
        answer: 'No. Recargas tu saldo y pagas solo lo que usas.',
      },
      {
        question: '¿Dónde puedo ver las tarifas?',
        answer: 'En la página de facturación de tu cuenta de Kizuna AI, que se abre con Recargar. Allí se muestran las tarifas antes de pagar.',
      },
      {
        question: 'La aplicación dice «Ya hay otra sesión activa en tu cuenta».',
        answer: 'Una cuenta ejecuta una sola sesión de KizunaAI a la vez, sea cual sea el motor que use. Detén la sesión en tu otro dispositivo o ventana y vuelve a intentarlo.',
      },
      {
        question: 'Al escuchar una voz de muestra, me pide que recargue.',
        answer: 'Las muestras de voz se sintetizan y se cobran de tu saldo, así que también necesitan saldo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Tienes dudas sobre un cargo? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Solde, coûts et durée de session',
  backLink: 'Retour au démarrage rapide KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Aperçu',
    content:
      'KizunaAI se paie à l’usage sur un solde prépayé de votre compte Kizuna AI, sans abonnement ; les deux moteurs, Soniox et Qwen, puisent dans ce solde à leurs propres tarifs. Cette page explique où voir votre solde, pourquoi Démarrer peut être désactivé, ce qui se passe quand une session atteint sa limite et quels réglages modifient le coût.',
  },
  steps: [
    {
      title: 'Consultez votre solde',
      content:
        'Le bouton du compte, dans la barre de titre, affiche votre solde. Cliquez dessus pour voir votre adresse e-mail, votre solde et vos dépenses des 30 derniers jours. <strong>Recharger</strong> ouvre dans votre navigateur la page de facturation de votre compte Kizuna AI, où vous ajoutez des fonds et pouvez consulter les tarifs en vigueur avant de payer.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Quand Démarrer est désactivé',
      content:
        'Sokuji ne démarre une session que si votre solde couvre au moins sa première minute. Sinon, le bouton Démarrer est grisé, le survoler affiche <strong>Solde insuffisant</strong> avec votre solde, et le bouton du compte porte un point rouge. Rechargez, puis réessayez.',
      screenshot: '02-start-blocked.png',
      tip: 'La traduction vocale nécessite un solde plus élevé pour démarrer que les sous-titres seuls, et plus encore quand l’autre partie vous est aussi lue à voix haute.',
    },
    {
      title: 'Soniox et Qwen ne sont pas facturés de la même façon',
      content:
        'Le moteur Soniox est facturé selon la durée pendant laquelle une session est connectée, la parole qu’il reconnaît et la parole qu’il synthétise. Le moteur Qwen est facturé au jeton : l’audio qu’il entend, le texte qu’il écrit et l’audio qu’il prononce, si bien qu’une session calme coûte peu et que la traduction vocale coûte plus cher que les sous-titres. Avec le contexte d’écran activé, le moteur Qwen est aussi facturé pour les images qu’il voit, à la seconde de parole, dans chaque sens. La région de Pékin de Qwen est tarifée en yuans chinois et convertie en dollars américains au taux du jour au moment du débit. Dans tous les cas, vous payez l’usage réel, et les tarifs figurent sur la page de facturation avant que vous ne rechargiez.',
    },
    {
      title: 'Pendant une session',
      content:
        'Une session s’appuie sur une partie de votre solde, et Sokuji affiche un compte à rebours du temps restant. Les longues sessions se déroulent en segments : quand l’un d’eux se termine, Sokuji indique <strong>Ce segment est terminé</strong> et vous cliquez sur <strong>Démarrer la session</strong> pour continuer. Quand votre solde est épuisé, la session s’arrête et vous invite à recharger.',
    },
    {
      title: 'Mode Les deux : session partagée ou sessions séparées',
      content:
        'Avec le moteur Soniox, en mode Les deux, <strong>Session partagée en mode Les deux</strong> (Paramètres → Avancé → Fournisseur) détermine le coût. <strong>Désactivé</strong> (par défaut) lance une session par sens, ce qui est plus fiable quand les deux personnes parlent en même temps, mais coûte environ deux fois plus cher par minute : votre solde dure donc environ deux fois moins longtemps, et un solde plus élevé est nécessaire pour démarrer. <strong>Activé</strong> traduit les deux côtés dans une seule session avec séparation automatique des voix, pour un coût et une latence réduits. Une conversation en face à face utilise toujours une seule session partagée. Le moteur Qwen n’a pas de session partagée : le mode Les deux lance toujours une session par sens.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Entendre l’autre partie à voix haute',
      content:
        'Avec <strong>Traduction que j\'entends</strong> activée (mode Autre ou Les deux), KizunaAI vous lit la traduction de l’autre partie avec une deuxième voix. Cette voix est facturée comme votre propre traduction vocale : la session coûte donc plus cher par minute et nécessite un solde plus élevé pour démarrer. Dans une conversation en face à face avec voix, les deux moitiés sont prononcées : votre traduction dans l’oreille de l’autre personne, et la sienne dans la vôtre.',
    },
  ],
  tips: {
    title: 'Comment dépenser moins',
    items: [
      'Lorsque vous n’avez besoin que de sous-titres, désactivez « Traduction entendue par l\'autre », et laissez « Traduction que j\'entends » désactivée sauf si vous voulez que l’autre partie vous soit lue à voix haute : la génération de la voix coûte plus cher.',
      'Avec le moteur Soniox, activez « Session partagée en mode Les deux » pour économiser, sauf si les deux côtés parlent souvent en même temps.',
      'Arrêtez la session à la fin de la conversation : une session en cours continue de consommer votre solde.',
      'Les comptes dont l’adresse e-mail est vérifiée reçoivent un crédit d’essai (actuellement 0,50 $ US, valable 7 jours).',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Y a-t-il un abonnement ?',
        answer: 'Non. Vous rechargez votre solde et ne payez que ce que vous utilisez.',
      },
      {
        question: 'Où voir les tarifs ?',
        answer: 'Sur la page de facturation de votre compte Kizuna AI, qu’ouvre Recharger. Les tarifs y sont affichés avant que vous ne payiez.',
      },
      {
        question: 'L’application indique « Une autre session est déjà en cours sur votre compte ».',
        answer: 'Un compte ne peut exécuter qu’une session KizunaAI à la fois, quel que soit le moteur utilisé. Arrêtez la session sur votre autre appareil ou fenêtre, puis réessayez.',
      },
      {
        question: 'Écouter une voix me demande de recharger.',
        answer: 'Les extraits de voix sont synthétisés et débités de votre solde : ils nécessitent donc eux aussi un solde.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Une question sur un débit ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'الرصيد والتكاليف ومدة الجلسة',
  backLink: 'العودة إلى البدء السريع مع KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'نظرة عامة',
    content:
      'يُدفع مقابل KizunaAI من رصيد مسبق الدفع في حساب Kizuna AI الخاص بك، حسب الاستخدام، دون اشتراك؛ ويسحب المحركان، Soniox وQwen، من هذا الرصيد بسعر خاص بكل منهما. تشرح هذه الصفحة أين ترى رصيدك، ولماذا قد يكون زر «ابدأ» معطّلًا، وماذا يحدث عندما تبلغ الجلسة حدّها، وأي الإعدادات تغيّر التكلفة.',
  },
  steps: [
    {
      title: 'تحقّق من رصيدك',
      content:
        'يعرض زر الحساب في شريط العنوان رصيدك. انقر عليه لرؤية عنوان بريدك الإلكتروني ورصيدك وما أنفقته خلال آخر 30 يومًا. يفتح <strong>شحن الرصيد</strong> صفحة الفوترة في حساب Kizuna AI الخاص بك في المتصفح، حيث تضيف الرصيد ويمكنك الاطلاع على الأسعار الحالية قبل الدفع.',
      screenshot: '01-balance.png',
    },
    {
      title: 'عندما يكون «ابدأ» معطّلًا',
      content:
        'لا يبدأ Sokuji جلسة إلا إذا كان رصيدك يغطي دقيقتها الأولى على الأقل. وإن لم يكن كذلك، يصبح زر «ابدأ» رماديًا، ويُظهر تمرير المؤشر فوقه <strong>رصيد غير كافٍ</strong> مع رصيدك، وتظهر نقطة حمراء على زر الحساب. اشحن رصيدك وحاول مرة أخرى.',
      screenshot: '02-start-blocked.png',
      tip: 'تحتاج الترجمة المنطوقة إلى رصيد أعلى للبدء مقارنةً بالترجمة النصية فقط، وإلى رصيد أعلى من ذلك عندما تُقرأ عليك ترجمة الطرف الآخر بصوت مسموع أيضًا.',
    },
    {
      title: 'يختلف احتساب Soniox وQwen',
      content:
        'يُحتسب محرك Soniox مقابل الوقت الذي تبقى فيه الجلسة متصلة، ومقابل الكلام الذي يتعرّف عليه، ومقابل الكلام الذي يولّده. أما محرك Qwen فيُحتسب لكل رمز (token): الصوت الذي يسمعه والنص الذي يكتبه والصوت الذي ينطقه، لذا تكلّف الجلسة الهادئة القليل، وتكلّف الترجمة المنطوقة أكثر من الترجمة النصية. وعند تفعيل سياق الشاشة يُحتسب محرك Qwen أيضًا مقابل الصور التي يراها، لكل ثانية كلام، في كل اتجاه. وتُسعَّر منطقة بكين في Qwen باليوان الصيني وتُحوَّل إلى الدولار الأمريكي بسعر اليوم عند احتساب الرسوم. وفي كل الأحوال تدفع مقابل الاستخدام الفعلي، وتوجد الأسعار في صفحة الفوترة قبل أن تشحن رصيدك.',
    },
    {
      title: 'أثناء الجلسة',
      content:
        'تعمل الجلسة على جزء من رصيدك، ويعدّ Sokuji الوقت المتبقي تنازليًا. تعمل الجلسات الطويلة على مقاطع: عندما ينتهي أحدها، يعرض Sokuji <strong>انتهى هذا المقطع</strong> وتنقر على <strong>بدء الجلسة</strong> للمتابعة. عندما ينفد رصيدك، تتوقف الجلسة وتطلب منك شحن الرصيد.',
    },
    {
      title: 'وضع الكل: جلسة مشتركة أم جلسات منفصلة',
      content:
        'مع محرك Soniox في وضع الكل، يحدد إعداد <strong>جلسة مشتركة في وضع الكل</strong> (الإعدادات ← متقدم ← المزود) التكلفة. مع <strong>معطل</strong> (الافتراضي) تعمل جلسة لكل اتجاه، وهو أكثر موثوقية عندما يتحدث الشخصان في الوقت نفسه، لكنه يكلّف نحو الضعف في الدقيقة، فيدوم رصيدك نحو نصف المدة ويلزم رصيد أعلى للبدء. أما مع <strong>مفعل</strong> فتترجم جلسة واحدة الطرفين مع فصل تلقائي للأصوات، بتكلفة وزمن تأخير أقل. وتستخدم المحادثة وجهًا لوجه دائمًا جلسة مشتركة واحدة. أما محرك Qwen فلا توجد فيه جلسة مشتركة: يشغّل وضع الكل دائمًا جلسة واحدة لكل اتجاه.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'سماع ترجمة الطرف الآخر بصوت مسموع',
      content:
        'عند تفعيل <strong>الترجمة التي أسمعها</strong> (في وضع الآخر أو الكل)، يقرأ عليك KizunaAI ترجمة الطرف الآخر بصوت ثانٍ. وتُحتسب تكلفة هذا الصوت مثل ترجمتك المنطوقة، لذا تكلّف الجلسة أكثر في الدقيقة وتحتاج إلى رصيد أعلى للبدء. وفي المحادثة وجهًا لوجه بالصوت، يُنطق النصفان كلاهما: ترجمتك في أذن الشخص الآخر، وترجمته في أذنك.',
    },
  ],
  tips: {
    title: 'طرق لتقليل الإنفاق',
    items: [
      'عندما تحتاج إلى الترجمة النصية فقط، أوقف «الترجمة التي يسمعها الطرف الآخر»، واترك «الترجمة التي أسمعها» متوقفة ما لم ترغب في سماع ترجمة الطرف الآخر بصوت مسموع: توليد الكلام يكلّف أكثر.',
      'مع محرك Soniox، فعّل «جلسة مشتركة في وضع الكل» للتوفير، ما لم يتحدث الطرفان كثيرًا في الوقت نفسه.',
      'أوقف الجلسة عند انتهاء المحادثة: فالجلسة الجارية تستمر في استهلاك رصيدك.',
      'تحصل الحسابات التي تم التحقق من بريدها الإلكتروني على رصيد تجريبي (حاليًا US$0.50، صالح لمدة 7 أيام).',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'هل يوجد اشتراك؟',
        answer: 'لا. تشحن رصيدك وتدفع مقابل ما تستخدمه فقط.',
      },
      {
        question: 'أين يمكنني رؤية الأسعار؟',
        answer: 'في صفحة الفوترة في حساب Kizuna AI الخاص بك، التي يفتحها «شحن الرصيد». تُعرض الأسعار هناك قبل أن تدفع.',
      },
      {
        question: 'يقول التطبيق «توجد جلسة أخرى قيد التشغيل بالفعل في حسابك».',
        answer: 'يشغّل الحساب الواحد جلسة KizunaAI واحدة في كل مرة، أيًّا كان المحرك الذي تستخدمه. أوقف الجلسة على جهازك أو نافذتك الأخرى، ثم حاول مرة أخرى.',
      },
      {
        question: 'تطلب مني معاينة صوت شحن الرصيد.',
        answer: 'تُولَّد معاينات الأصوات وتُخصم من رصيدك، لذا تحتاج هي أيضًا إلى رصيد.',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'لديك أسئلة بشأن رسوم ما؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Saldo, custos e tempo de sessão',
  backLink: 'Voltar ao início rápido com KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Visão geral',
    content:
      'A KizunaAI é paga com um saldo pré-pago na sua conta Kizuna AI, por uso, sem assinatura; os dois mecanismos, Soniox e Qwen, usam esse saldo, cada um com as suas tarifas. Esta página explica onde ver o seu saldo, por que Iniciar pode estar desativado, o que acontece quando uma sessão atinge o limite e quais configurações mudam o custo.',
  },
  steps: [
    {
      title: 'Confira seu saldo',
      content:
        'O botão da conta, na barra de título, mostra seu saldo. Clique nele para ver seu endereço de e-mail, seu saldo e quanto você gastou nos últimos 30 dias. <strong>Recarregar</strong> abre no navegador a página de cobrança da sua conta Kizuna AI, onde você adiciona fundos e vê as tarifas atuais antes de pagar.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Quando Iniciar está desativado',
      content:
        'O Sokuji só inicia uma sessão quando o seu saldo cobre pelo menos o primeiro minuto dela. Caso contrário, o botão Iniciar fica cinza, passar o mouse sobre ele mostra <strong>Saldo insuficiente</strong> com o seu saldo, e o botão da conta exibe um ponto vermelho. Recarregue e tente de novo.',
      screenshot: '02-start-blocked.png',
      tip: 'A tradução falada exige um saldo maior para iniciar do que só legendas, e maior ainda quando a tradução da outra pessoa também é lida em voz alta para você.',
    },
    {
      title: 'A Soniox e a Qwen são medidas de formas diferentes',
      content:
        'O mecanismo Soniox é cobrado pelo tempo em que uma sessão fica conectada, pela fala que reconhece e pela fala que sintetiza. O mecanismo Qwen é cobrado por token: o áudio que ouve, o texto que escreve e o áudio que fala; assim, uma sessão silenciosa custa pouco e a tradução falada custa mais do que só legendas. Com o contexto da tela ativado, o mecanismo Qwen também é cobrado pelas imagens que vê, por segundo de fala, em cada direção. A região de Pequim da Qwen tem preço em yuan chinês e é convertida em dólares americanos pela cotação do dia em que a cobrança é feita. Em todos os casos você paga pelo uso real, e as tarifas ficam na página de cobrança antes de você recarregar.',
    },
    {
      title: 'Durante uma sessão',
      content:
        'Uma sessão usa uma parte do seu saldo, e o Sokuji faz a contagem regressiva do tempo restante. Sessões longas são executadas em segmentos: quando um termina, o Sokuji diz <strong>Este segmento terminou</strong> e você clica em <strong>Iniciar sessão</strong> para continuar. Quando o seu saldo acaba, a sessão para e pede que você recarregue.',
    },
    {
      title: 'Modo Ambos: sessão compartilhada ou separada',
      content:
        'Com o mecanismo Soniox, no modo Ambos, <strong>Sessão compartilhada no modo Ambos</strong> (Configurações → Avançado → Provedor) define o custo. <strong>Desabilitado</strong> (o padrão) executa uma sessão por direção, o que é mais confiável quando as duas pessoas falam ao mesmo tempo, mas custa cerca do dobro por minuto; assim, o seu saldo dura cerca de metade do tempo e é preciso um saldo maior para iniciar. <strong>Ativado</strong> traduz os dois lados em uma única sessão com separação automática de vozes, com custo e latência menores. Uma conversa cara a cara sempre usa uma única sessão compartilhada. O mecanismo Qwen não tem sessão compartilhada: o modo Ambos sempre executa uma sessão por direção.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Ouvir a outra pessoa em voz alta',
      content:
        'Com <strong>Tradução que eu ouço</strong> ativada (modo Outro ou Ambos), a KizunaAI lê para você a tradução da outra pessoa com uma segunda voz. Essa voz é cobrada como a sua própria tradução falada, então a sessão custa mais por minuto e exige um saldo maior para iniciar. Em uma conversa cara a cara com voz, as duas metades são faladas: a sua tradução no ouvido da outra pessoa, e a dela no seu.',
    },
  ],
  tips: {
    title: 'Formas de gastar menos',
    items: [
      'Quando precisar só de legendas, desative Tradução que o outro lado ouve e deixe Tradução que eu ouço desativada, a menos que queira ouvir a outra pessoa em voz alta: gerar fala custa mais.',
      'Com o mecanismo Soniox, ative Sessão compartilhada no modo Ambos para economizar, a menos que os dois lados falem ao mesmo tempo com frequência.',
      'Pare a sessão quando a conversa terminar: uma sessão em andamento continua consumindo seu saldo.',
      'Contas com endereço de e-mail verificado recebem um crédito de teste (atualmente US$ 0,50, válido por 7 dias).',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Existe assinatura?',
        answer: 'Não. Você recarrega seu saldo e paga só pelo que usar.',
      },
      {
        question: 'Onde vejo as tarifas?',
        answer: 'Na página de cobrança da sua conta Kizuna AI, que Recarregar abre. As tarifas aparecem lá antes de você pagar.',
      },
      {
        question: 'O app diz “Já existe outra sessão em andamento na sua conta”.',
        answer: 'Uma conta executa uma sessão KizunaAI por vez, seja qual for o mecanismo. Pare a sessão no seu outro dispositivo ou janela e tente de novo.',
      },
      {
        question: 'Ouvir a prévia de uma voz pede que eu recarregue.',
        answer: 'As prévias de voz são sintetizadas e cobradas do seu saldo, então também exigem saldo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Dúvidas sobre uma cobrança? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Баланс, расходы и время сеанса',
  backLink: 'Назад к быстрому старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Обзор',
    content:
      'KizunaAI оплачивается с предоплаченного баланса вашей учётной записи Kizuna AI по факту использования, без подписки; оба движка, Soniox и Qwen, списывают средства с этого баланса по своим тарифам. На этой странице объясняется, где посмотреть баланс, почему кнопка «Старт» может быть недоступна, что происходит, когда сеанс достигает предела, и какие настройки влияют на стоимость.',
  },
  steps: [
    {
      title: 'Проверьте баланс',
      content:
        'Кнопка учётной записи в строке заголовка показывает ваш баланс. Нажмите её, чтобы увидеть свой адрес электронной почты, баланс и расходы за последние 30 дней. <strong>Пополнить</strong> открывает в браузере страницу оплаты вашей учётной записи Kizuna AI: там вы вносите средства и перед оплатой видите текущие тарифы.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Когда кнопка «Старт» недоступна',
      content:
        'Sokuji начинает сеанс, только если баланса хватает хотя бы на его первую минуту. Если не хватает, кнопка «Старт» становится серой, при наведении на неё отображается <strong>Недостаточный баланс</strong> с суммой баланса, а на кнопке учётной записи появляется красная точка. Пополните баланс и попробуйте снова.',
      screenshot: '02-start-blocked.png',
      tip: 'Для начала сеанса с озвученным переводом нужен больший баланс, чем только для субтитров, и ещё больший, когда перевод собеседника тоже зачитывается вам вслух.',
    },
    {
      title: 'Soniox и Qwen тарифицируются по-разному',
      content:
        'Движок Soniox оплачивается за время, пока сеанс подключён, за распознанную речь и за синтезированную речь. Движок Qwen оплачивается по токенам: за звук, который он слышит, текст, который он пишет, и звук, который он произносит, поэтому тихий сеанс стоит недорого, а озвученный перевод обходится дороже, чем одни субтитры. Когда включён контекст экрана, движок Qwen оплачивается ещё и за изображения, которые он видит, — за каждую секунду речи в каждом направлении. Регион Qwen в Пекине тарифицируется в китайских юанях и при списании пересчитывается в доллары США по курсу дня. В любом случае вы платите за фактическое использование, а тарифы указаны на странице оплаты до пополнения баланса.',
    },
    {
      title: 'Во время сеанса',
      content:
        'Сеанс работает за счёт части вашего баланса, и Sokuji ведёт обратный отсчёт оставшегося времени. Длинные сеансы идут сегментами: когда сегмент заканчивается, Sokuji сообщает <strong>Этот сегмент завершён</strong>, и вы нажимаете <strong>Начать сессию</strong>, чтобы продолжить. Когда баланс исчерпан, сеанс останавливается и предлагает пополнить баланс.',
    },
    {
      title: 'Режим «Оба»: общий или раздельные сеансы',
      content:
        'С движком Soniox в режиме «Оба» стоимость определяет переключатель <strong>Общий сеанс в режиме «Оба»</strong> (Настройки → Расширенный → Поставщик). <strong>Отключено</strong> (по умолчанию) — для каждого направления запускается отдельный сеанс; это надёжнее, когда оба говорят одновременно, но стоит примерно вдвое дороже в минуту, поэтому баланса хватает примерно вдвое меньше, а для начала сеанса нужен больший баланс. <strong>Включено</strong> — обе стороны переводятся в одном сеансе с автоматическим разделением голосов, дешевле и с меньшей задержкой. Разговор лицом к лицу всегда использует один общий сеанс. У движка Qwen общего сеанса нет: режим «Оба» всегда запускает по одному сеансу на каждое направление.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Перевод собеседника вслух',
      content:
        'Когда включён <strong>Перевод, который слышу я</strong> (режим «Собеседник» или «Оба»), KizunaAI зачитывает вам перевод собеседника вторым голосом. Этот голос оплачивается так же, как ваш собственный озвученный перевод, поэтому минута сеанса стоит дороже и для его начала нужен больший баланс. В разговоре лицом к лицу с голосом озвучиваются обе половины: ваш перевод — в ухе собеседника, а его — в вашем.',
    },
  ],
  tips: {
    title: 'Как тратить меньше',
    items: [
      'Если вам нужны только субтитры, выключите «Перевод, который слышит собеседник» и оставьте выключенным «Перевод, который слышу я», если только вы не хотите слышать перевод собеседника вслух: синтез речи стоит дополнительно.',
      'С движком Soniox включите «Общий сеанс в режиме «Оба»», чтобы сэкономить, если только обе стороны не говорят часто одновременно.',
      'Останавливайте сеанс, когда разговор закончен: запущенный сеанс продолжает расходовать баланс.',
      'Учётные записи с подтверждённым адресом электронной почты получают пробный кредит (сейчас US$0.50, действует 7 дней).',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Есть ли подписка?',
        answer: 'Нет. Вы пополняете баланс и платите только за то, что используете.',
      },
      {
        question: 'Где посмотреть тарифы?',
        answer: 'На странице оплаты вашей учётной записи Kizuna AI, которую открывает кнопка «Пополнить». Там тарифы показаны до оплаты.',
      },
      {
        question: 'Приложение пишет «На вашем аккаунте уже выполняется другой сеанс».',
        answer: 'На одной учётной записи одновременно может идти только один сеанс KizunaAI, какой бы движок он ни использовал. Остановите сеанс на другом устройстве или в другом окне и попробуйте снова.',
      },
      {
        question: 'При прослушивании голоса предлагается пополнить баланс.',
        answer: 'Прослушивания голосов синтезируются и списываются с баланса, поэтому для них тоже нужен баланс.',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Есть вопросы о списании? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'Guthaben, Kosten und Sitzungsdauer',
  backLink: 'Zurück zum KizunaAI-Schnellstart',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Übersicht',
    content:
      'KizunaAI wird nutzungsbasiert aus einem Prepaid-Guthaben auf Ihrem Kizuna AI-Konto bezahlt, ohne Abonnement; beide Engines, Soniox und Qwen, greifen zu ihren eigenen Preisen auf dieses Guthaben zu. Diese Seite erklärt, wo Sie Ihr Guthaben sehen, warum „Starten“ deaktiviert sein kann, was passiert, wenn eine Sitzung ihr Limit erreicht, und welche Einstellungen die Kosten verändern.',
  },
  steps: [
    {
      title: 'Guthaben prüfen',
      content:
        'Die Konto-Schaltfläche in der Titelleiste zeigt Ihr Guthaben. Klicken Sie darauf, um Ihre E-Mail-Adresse, Ihr Guthaben und Ihre Ausgaben der letzten 30 Tage zu sehen. <strong>Aufladen</strong> öffnet die Abrechnungsseite Ihres Kizuna AI-Kontos im Browser; dort laden Sie Guthaben auf und sehen vor dem Bezahlen die aktuellen Preise.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Wenn „Starten“ deaktiviert ist',
      content:
        'Sokuji startet eine Sitzung nur, wenn Ihr Guthaben mindestens deren erste Minute abdeckt. Ist das nicht der Fall, ist die Schaltfläche „Starten“ ausgegraut, beim Darüberfahren erscheint <strong>Unzureichendes Guthaben</strong> mit Ihrem Guthaben, und die Konto-Schaltfläche trägt einen roten Punkt. Laden Sie auf und versuchen Sie es erneut.',
      screenshot: '02-start-blocked.png',
      tip: 'Für gesprochene Übersetzung ist zum Starten ein höheres Guthaben nötig als für reine Untertitel, und ein noch höheres, wenn Ihnen auch die andere Seite vorgelesen wird.',
    },
    {
      title: 'Soniox und Qwen werden unterschiedlich abgerechnet',
      content:
        'Die Soniox-Engine wird für die Zeit berechnet, in der eine Sitzung verbunden ist, für die Sprache, die sie erkennt, und für die Sprache, die sie synthetisiert. Die Qwen-Engine wird pro Token berechnet: für das Audio, das sie hört, den Text, den sie schreibt, und das Audio, das sie spricht; eine ruhige Sitzung kostet also wenig, und gesprochene Übersetzung kostet mehr als Untertitel. Bei eingeschaltetem Bildschirmkontext wird die Qwen-Engine außerdem für die Bilder berechnet, die sie sieht, pro Sekunde Sprache und für jede Richtung. Die Region Peking von Qwen wird in chinesischen Yuan bepreist und bei der Abbuchung zum Tageskurs in US-Dollar umgerechnet. In jedem Fall zahlen Sie für die tatsächliche Nutzung, und die Preise stehen vor dem Aufladen auf der Abrechnungsseite.',
    },
    {
      title: 'Während einer Sitzung',
      content:
        'Eine Sitzung läuft auf einem Teil Ihres Guthabens, und Sokuji zählt die verbleibende Zeit herunter. Lange Sitzungen laufen in Abschnitten: Wenn einer endet, meldet Sokuji <strong>Dieser Abschnitt ist beendet</strong>, und Sie klicken auf <strong>Sitzung starten</strong>, um fortzufahren. Ist Ihr Guthaben aufgebraucht, endet die Sitzung und fordert Sie zum Aufladen auf.',
    },
    {
      title: 'Modus „Beides“: gemeinsame oder getrennte Sitzungen',
      content:
        'Mit der Soniox-Engine bestimmt im Modus „Beides“ <strong>Gemeinsame Sitzung im Modus „Beides“</strong> (Einstellungen → Erweitert → Anbieter) die Kosten. <strong>Deaktiviert</strong> (Standard) führt pro Richtung eine eigene Sitzung aus. Das ist zuverlässiger, wenn beide gleichzeitig sprechen, kostet aber pro Minute etwa doppelt so viel; Ihr Guthaben reicht dann etwa halb so lange, und zum Starten ist ein höheres Guthaben nötig. <strong>Aktiviert</strong> übersetzt beide Seiten in einer Sitzung mit automatischer Stimmtrennung, zu geringeren Kosten und mit geringerer Latenz. Ein Gespräch von Angesicht zu Angesicht verwendet immer eine gemeinsame Sitzung. Die Qwen-Engine hat keine gemeinsame Sitzung: Der Modus „Beides“ führt immer pro Richtung eine eigene Sitzung aus.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Die andere Seite vorgelesen hören',
      content:
        'Ist <strong>Übersetzung, die ich höre</strong> eingeschaltet (Modus „Gegenüber“ oder „Beides“), liest Ihnen KizunaAI die Übersetzung der anderen Seite mit einer zweiten Stimme vor. Diese Stimme wird wie Ihre eigene gesprochene Übersetzung abgerechnet, daher kostet die Sitzung pro Minute mehr und braucht zum Starten ein höheres Guthaben. Bei einem Gespräch von Angesicht zu Angesicht mit Stimme werden beide Hälften gesprochen: Ihre Übersetzung im Ohr der anderen Person und deren Übersetzung in Ihrem.',
    },
  ],
  tips: {
    title: 'So geben Sie weniger aus',
    items: [
      'Wenn Sie nur Untertitel brauchen, schalten Sie „Übersetzung, die das Gegenüber hört“ aus und lassen „Übersetzung, die ich höre“ ausgeschaltet, außer Sie möchten sich die andere Seite vorlesen lassen: Die Sprachausgabe kostet extra.',
      'Schalten Sie mit der Soniox-Engine »Gemeinsame Sitzung im Modus „Beides“« ein, um zu sparen, außer beide Seiten sprechen oft gleichzeitig.',
      'Beenden Sie die Sitzung, wenn das Gespräch vorbei ist: Eine laufende Sitzung verbraucht weiter Guthaben.',
      'Konten mit bestätigter E-Mail-Adresse erhalten ein Testguthaben (derzeit US$0.50, 7 Tage gültig).',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Gibt es ein Abonnement?',
        answer: 'Nein. Sie laden Ihr Guthaben auf und zahlen nur für das, was Sie nutzen.',
      },
      {
        question: 'Wo sehe ich die Preise?',
        answer: 'Auf der Abrechnungsseite Ihres Kizuna AI-Kontos, die „Aufladen“ öffnet. Dort werden die Preise angezeigt, bevor Sie bezahlen.',
      },
      {
        question: 'Die App meldet „In Ihrem Konto läuft bereits eine andere Sitzung“.',
        answer: 'Ein Konto kann jeweils nur eine KizunaAI-Sitzung ausführen, mit welcher Engine auch immer. Beenden Sie die Sitzung auf Ihrem anderen Gerät oder in Ihrem anderen Fenster und versuchen Sie es dann erneut.',
      },
      {
        question: 'Beim Anhören einer Stimme werde ich zum Aufladen aufgefordert.',
        answer: 'Hörproben von Stimmen werden synthetisiert und von Ihrem Guthaben abgebucht, daher brauchen auch sie Guthaben.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Fragen zu einer Abbuchung? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Баланс, витрати й тривалість сеансу',
  backLink: 'Назад до швидкого старту KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Огляд',
    content:
      'KizunaAI оплачується з передплаченого балансу вашого облікового запису Kizuna AI за фактичне використання, без підписки; обидва рушії, Soniox і Qwen, списують кошти з цього балансу за власними тарифами. На цій сторінці пояснено, де переглянути баланс, чому кнопка «Почати» може бути недоступною, що відбувається, коли сеанс досягає ліміту, і які налаштування впливають на вартість.',
  },
  steps: [
    {
      title: 'Перевірте баланс',
      content:
        'Кнопка облікового запису в рядку заголовка показує ваш баланс. Натисніть її, щоб побачити свою адресу електронної пошти, баланс і витрати за останні 30 днів. <strong>Поповнити</strong> відкриває в браузері сторінку оплати вашого облікового запису Kizuna AI: там ви вносите кошти й перед оплатою бачите поточні тарифи.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Коли кнопка «Почати» недоступна',
      content:
        'Sokuji починає сеанс, лише якщо балансу вистачає принаймні на його першу хвилину. Якщо не вистачає, кнопка «Почати» стає сірою, під час наведення на неї з’являється <strong>Недостатній баланс</strong> із сумою балансу, а на кнопці облікового запису з’являється червона крапка. Поповніть баланс і спробуйте ще раз.',
      screenshot: '02-start-blocked.png',
      tip: 'Щоб почати сеанс з озвученим перекладом, потрібен більший баланс, ніж для самих лише субтитрів, і ще більший, коли переклад співрозмовника теж зачитується вам уголос.',
    },
    {
      title: 'Soniox і Qwen тарифікуються по-різному',
      content:
        'Рушій Soniox оплачується за час, поки сеанс підключений, за розпізнане мовлення та за синтезоване мовлення. Рушій Qwen оплачується за токени: за звук, який він чує, текст, який пише, і звук, який промовляє, тож тихий сеанс коштує мало, а озвучений переклад дорожчий за субтитри. Коли ввімкнено контекст екрана, рушій Qwen оплачується ще й за зображення, які він бачить, — за кожну секунду мовлення в кожному напрямку. Регіон Qwen у Пекіні тарифікується в китайських юанях і за курсом дня переводиться в долари США в момент списання. У кожному разі ви платите за фактичне використання, а тарифи показано на сторінці оплати ще до поповнення.',
    },
    {
      title: 'Під час сеансу',
      content:
        'Сеанс працює за рахунок частини вашого балансу, і Sokuji веде зворотний відлік часу, що залишився. Довгі сеанси йдуть сегментами: коли сегмент закінчується, Sokuji повідомляє <strong>Цей сегмент завершено</strong>, і ви натискаєте <strong>Почати сесію</strong>, щоб продовжити. Коли баланс вичерпано, сеанс зупиняється й пропонує поповнити баланс.',
    },
    {
      title: 'Режим «Обидва»: спільний чи окремі сеанси',
      content:
        'З рушієм Soniox у режимі «Обидва» вартість визначає перемикач <strong>Спільний сеанс у режимі «Обидва»</strong> (Налаштування → Розширений → Постачальник). <strong>Вимкнено</strong> (за замовчуванням) — для кожного напрямку запускається окремий сеанс; це надійніше, коли обоє говорять одночасно, але коштує приблизно вдвічі дорожче за хвилину, тож балансу вистачає приблизно вдвічі менше, а для початку сеансу потрібен більший баланс. <strong>Увімкнено</strong> — обидві сторони перекладаються в одному сеансі з автоматичним розділенням голосів, дешевше й з меншою затримкою. Розмова віч-на-віч завжди використовує один спільний сеанс. Рушій Qwen спільного сеансу не має: режим «Обидва» завжди запускає окремий сеанс для кожного напрямку.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Коли переклад співрозмовника зачитується вголос',
      content:
        'Якщо ввімкнено <strong>Переклад, який чую я</strong> (режим «Співрозмовник» або «Обидва»), KizunaAI зачитує вам переклад співрозмовника другим голосом. Цей голос оплачується так само, як ваш власний озвучений переклад, тому сеанс коштує дорожче за хвилину, а для його початку потрібен більший баланс. У розмові віч-на-віч із голосом озвучуються обидві половини: ваш переклад — у вусі співрозмовника, а його — у вашому.',
    },
  ],
  tips: {
    title: 'Як витрачати менше',
    items: [
      'Коли вам потрібні лише субтитри, вимкніть «Переклад, який чує співрозмовник», а «Переклад, який чую я» залиште вимкненим, якщо не хочете, щоб переклад співрозмовника зачитувався вголос: синтез мовлення коштує додатково.',
      'З рушієм Soniox, щоб заощадити, увімкніть «Спільний сеанс у режимі «Обидва»», якщо тільки обидві сторони не говорять часто одночасно.',
      'Зупиняйте сеанс, коли розмову закінчено: запущений сеанс і далі витрачає баланс.',
      'Облікові записи з підтвердженою адресою електронної пошти отримують пробний баланс (зараз US$0.50, діє 7 днів).',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Чи є підписка?',
        answer: 'Ні. Ви поповнюєте баланс і платите лише за те, що використовуєте.',
      },
      {
        question: 'Де переглянути тарифи?',
        answer: 'На сторінці оплати вашого облікового запису Kizuna AI, яку відкриває кнопка «Поповнити». Там тарифи показано ще до оплати.',
      },
      {
        question: 'Застосунок пише «На вашому обліковому записі вже виконується інший сеанс».',
        answer: 'В одному обліковому записі одночасно може працювати лише один сеанс KizunaAI, незалежно від рушія. Зупиніть сеанс на іншому пристрої чи в іншому вікні й спробуйте ще раз.',
      },
      {
        question: 'Під час прослуховування голосу мені пропонують поповнити баланс.',
        answer: 'Прослуховування голосів синтезуються й списуються з балансу, тому для них теж потрібен баланс.',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Маєте запитання щодо списання? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Saldo, costi e durata della sessione',
  backLink: 'Torna alla guida rapida a KizunaAI',
  backLinkUrl: '/docs/kizuna-ai/quick-start',
  overview: {
    title: 'Panoramica',
    content:
      'KizunaAI si paga da un saldo prepagato sul tuo account Kizuna AI, in base all’uso e senza abbonamento; entrambi i motori, Soniox e Qwen, attingono a quel saldo alle rispettive tariffe. Questa pagina spiega dove vedere il saldo, perché Avvia può essere disattivato, cosa succede quando una sessione raggiunge il limite e quali impostazioni cambiano il costo.',
  },
  steps: [
    {
      title: 'Controlla il saldo',
      content:
        'Il pulsante dell’account nella barra del titolo mostra il tuo saldo. Fai clic per vedere il tuo indirizzo email, il saldo e quanto hai speso negli ultimi 30 giorni. <strong>Ricarica</strong> apre nel browser la pagina di fatturazione del tuo account Kizuna AI, dove aggiungi fondi e vedi le tariffe attuali prima di pagare.',
      screenshot: '01-balance.png',
    },
    {
      title: 'Quando Avvia è disattivato',
      content:
        'Sokuji avvia una sessione solo se il saldo copre almeno il suo primo minuto. Altrimenti il pulsante Avvia è grigio, passandoci sopra con il mouse compare <strong>Saldo insufficiente</strong> con il tuo saldo, e il pulsante dell’account mostra un punto rosso. Ricarica e riprova.',
      screenshot: '02-start-blocked.png',
      tip: 'Per avviare la traduzione vocale serve un saldo più alto che per i soli sottotitoli, e ancora di più quando anche l’altra parte ti viene letta ad alta voce.',
    },
    {
      title: 'Soniox e Qwen sono misurati in modo diverso',
      content:
        'Il motore Soniox viene addebitato per il tempo in cui una sessione resta connessa, per il parlato che riconosce e per il parlato che sintetizza. Il motore Qwen viene addebitato a token: l’audio che ascolta, il testo che scrive e l’audio che pronuncia, quindi una sessione silenziosa costa poco e la traduzione vocale costa più dei sottotitoli. Con il contesto dello schermo attivo, il motore Qwen viene addebitato anche per le immagini che vede, per ogni secondo di parlato, in ogni direzione. La regione di Pechino di Qwen ha i prezzi in yuan cinesi, convertiti in dollari USA al cambio del giorno al momento dell’addebito. In ogni caso paghi l’uso effettivo, e le tariffe sono sulla pagina di fatturazione prima di ricaricare.',
    },
    {
      title: 'Durante una sessione',
      content:
        'Una sessione usa una parte del tuo saldo, e Sokuji mostra il conto alla rovescia del tempo rimasto. Le sessioni lunghe procedono per segmenti: quando uno finisce, Sokuji indica <strong>Questo segmento è terminato</strong> e fai clic su <strong>Inizia sessione</strong> per continuare. Quando il saldo è esaurito, la sessione si interrompe e ti chiede di ricaricare.',
    },
    {
      title: 'Modalità Entrambi: sessione condivisa o separata',
      content:
        'Con il motore Soniox in modalità Entrambi, <strong>Sessione condivisa in modalità Entrambi</strong> (Impostazioni → Avanzato → Fornitore) determina il costo. <strong>Disabilitato</strong> (predefinito) esegue una sessione per ogni direzione: è più affidabile quando entrambe le persone parlano insieme, ma costa circa il doppio al minuto, quindi il saldo dura circa la metà e per avviare serve un saldo più alto. <strong>Abilitato</strong> traduce entrambe le parti in una sola sessione con separazione automatica delle voci, con costi e latenza inferiori. Una conversazione faccia a faccia usa sempre una sola sessione condivisa. Il motore Qwen non ha una sessione condivisa: la modalità Entrambi esegue sempre una sessione per direzione.',
      screenshot: '03-shared-session.png',
    },
    {
      title: 'Sentire l’altra parte letta ad alta voce',
      content:
        'Con <strong>Traduzione che sento io</strong> attivata (modalità Altro o Entrambi), KizunaAI ti legge la traduzione dell’altra parte con una seconda voce. Quella voce viene addebitata come la tua traduzione vocale, quindi la sessione costa di più al minuto e per avviarla serve un saldo più alto. In una conversazione faccia a faccia con voce vengono pronunciate entrambe le metà: la tua traduzione nell’orecchio dell’altra persona, e la sua nel tuo.',
    },
  ],
  tips: {
    title: 'Come spendere meno',
    items: [
      'Quando ti servono solo i sottotitoli, disattiva Traduzione che sente l\'altra parte, e lascia disattivata Traduzione che sento io a meno che tu non voglia sentire l’altra parte letta ad alta voce: generare la voce costa di più.',
      'Con il motore Soniox, attiva Sessione condivisa in modalità Entrambi per risparmiare, a meno che le due parti non parlino spesso nello stesso momento.',
      'Interrompi la sessione quando la conversazione finisce: una sessione attiva continua a consumare il saldo.',
      'Gli account con un indirizzo email verificato ricevono un credito di prova (attualmente 0,50 US$, valido 7 giorni).',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'C’è un abbonamento?',
        answer: 'No. Ricarichi il saldo e paghi solo quello che usi.',
      },
      {
        question: 'Dove vedo le tariffe?',
        answer: 'Nella pagina di fatturazione del tuo account Kizuna AI, che si apre con Ricarica. Lì le tariffe sono indicate prima di pagare.',
      },
      {
        question: 'L’app dice «Un\'altra sessione è già attiva sul tuo account».',
        answer: 'Un account esegue una sola sessione KizunaAI alla volta, qualunque motore usi. Interrompi la sessione sull’altro dispositivo o nell’altra finestra, poi riprova.',
      },
      {
        question: 'Ascoltare l’anteprima di una voce mi chiede di ricaricare.',
        answer: 'Le anteprime delle voci vengono sintetizzate e addebitate sul saldo, quindi anche loro richiedono un saldo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Domande su un addebito? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaBillingGuide() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-billing" />;
}
